/**
 * When the period starts, under "Keep for" and on the document's page. The
 * store starts it when the document is finished (completeEnvelopeTx), and a
 * document is finished only once every signature is in and every party asked
 * to open it has opened it. It said "Starts when everyone has signed", which
 * is false while a viewer has still to open the document, and the customer
 * docs (send-a-document, templates) already said the finished rule.
 */
export const RETENTION_STARTS =
  'Starts when the document is finished: every signature is in, and everyone asked to open it has opened it. Your copy of the PDF still works after that.'

/** The periods "Keep for" has always offered, in years. Sent as years times 365 days. */
const KEEP_FOR_YEARS = [1, 3, 7, 10]

export interface KeepForChoice { years: number; label: string }

const daysOf = (years: number) => Math.round(years * 365)

const binds = (maxDays?: number): maxDays is number =>
  typeof maxDays === 'number' && Number.isFinite(maxDays) && maxDays > 0

const labelFor = (years: number): string =>
  Number.isInteger(years) ? (years === 1 ? '1 year' : `${years} years`) : `${daysOf(years)} days`

/**
 * The default when nothing is chosen. A template's own period (templateDays)
 * when the organization may keep a document that long; without one, seven
 * years when it may; otherwise the longest it may. The same rule as the
 * phone app's (M16, and final plan 4.11 for a template).
 */
function defaultYears(maxDays: number | undefined, templateDays: number | undefined, choices: KeepForChoice[]): number {
  const longest = choices[choices.length - 1].years
  if (typeof templateDays === 'number' && Number.isFinite(templateDays) && templateDays > 0) {
    return !binds(maxDays) || templateDays <= maxDays ? templateDays / 365 : longest
  }
  return choices.some((c) => c.years === 7) ? 7 : longest
}

/**
 * The periods an organization can keep a document for: every one of the four
 * that fits under its ceiling (the session's max_retention_days), or all four
 * when nothing limits it, which is what billing off means. The gate refuses a
 * named period above the ceiling, and the page used to offer seven and ten
 * years to a member whose plan keeps one, and to default to seven, so the
 * send was refused. No plan today keeps less than a year; one that did would
 * be offered its own period rather than nothing, as the phone app does. A
 * template's own period that is none of the four is offered beside them, so
 * the default is always one the sender can see.
 */
export function keepForChoices(maxDays?: number, templateDays?: number): KeepForChoice[] {
  const all = KEEP_FOR_YEARS.map((years) => ({ years, label: labelFor(years) }))
  const fits = binds(maxDays) ? all.filter((c) => daysOf(c.years) <= maxDays) : all
  const choices = fits.length > 0 ? fits : [{ years: maxDays! / 365, label: `${maxDays} days` }]
  const fallback = defaultYears(maxDays, templateDays, choices)
  if (choices.some((c) => daysOf(c.years) === daysOf(fallback))) return choices
  return [...choices, { years: fallback, label: labelFor(fallback) }].sort((a, b) => a.years - b.years)
}

/**
 * What a document is kept for, in years: the sender's choice when the
 * organization may keep it that long, otherwise the default (defaultYears).
 * A choice made for one organization does not survive a move to one that
 * cannot keep it.
 */
export function keptFor(maxDays: number | undefined, chosen: number | null, templateDays?: number): number {
  const choices = keepForChoices(maxDays, templateDays)
  if (chosen !== null) {
    const pick = choices.find((c) => daysOf(c.years) === daysOf(chosen))
    if (pick && (!binds(maxDays) || daysOf(chosen) <= maxDays)) return pick.years
  }
  const fallback = daysOf(defaultYears(maxDays, templateDays, choices))
  return (choices.find((c) => daysOf(c.years) === fallback) ?? choices[choices.length - 1]).years
}

/** The days a send carries for a period in years. */
export const retentionDaysOf = daysOf
